import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Loader2 } from 'lucide-react';

const CATEGORIES = ['Travel', 'Accommodation', 'Meals', 'Transportation', 'Office Supplies', 'Equipment', 'Training', 'Software', 'Communication', 'Other'];
const CURRENCIES = ['USD', 'AED', 'INR', 'GBP', 'SGD', 'GHS', 'KES', 'UGX', 'XAF', 'MUR'];

const MyExpenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptB64, setReceiptB64] = useState('');
  const [extraAttachments, setExtraAttachments] = useState([]);
  const [form, setForm] = useState({ category: '', amount: '', currency: 'USD', description: '' });

  const fetchAll = async () => {
    try { const r = await api.get('/me/expenses'); setExpenses(r.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setReceiptFile(f);
    const r = new FileReader();
    r.onload = (ev) => setReceiptB64(ev.target.result);
    r.readAsDataURL(f);
  };

  const handleExtraAttachments = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const valid = files.filter(f => f.size <= 5 * 1024 * 1024);
    if (valid.length < files.length) toast.error('Some files exceeded 5MB and were skipped');
    const encoded = await Promise.all(valid.map(f => new Promise((resolve) => {
      const r = new FileReader();
      r.onload = (ev) => resolve({ name: f.name, mime: f.type, content_base64: ev.target.result });
      r.readAsDataURL(f);
    })));
    setExtraAttachments([...extraAttachments, ...encoded]);
    e.target.value = '';
  };

  const submit = async () => {
    if (!form.category || !form.amount) { toast.error('Fill all required fields'); return; }
    if (!receiptB64) { toast.error('Receipt is mandatory'); return; }
    setSubmitting(true);
    try {
      await api.post('/me/expenses', {
        ...form,
        amount: parseFloat(form.amount),
        receipt_base64: receiptB64,
        attachments: extraAttachments,
        employee_id: 'self',
      });
      toast.success('Expense submitted');
      setDialogOpen(false);
      setForm({ category: '', amount: '', currency: 'USD', description: '' });
      setReceiptFile(null); setReceiptB64(''); setExtraAttachments([]);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="my-expenses-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">My Expenses</h1>
          <p className="text-muted-foreground">Submit and track your expense claims</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button data-testid="submit-expense-btn"><Plus size={16} className="mr-2" />New Expense</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Submit Expense Claim</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger data-testid="exp-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} data-testid="exp-amount" /></div>
                <div><Label>Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({...form, currency: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
              <div>
                <Label>Receipt <span className="text-destructive">*</span></Label>
                <Input type="file" accept="image/*,application/pdf" onChange={handleFile} data-testid="exp-receipt" />
                {receiptFile ? <p className="text-xs text-emerald-600 mt-1">✓ {receiptFile.name}</p> : <p className="text-xs text-amber-600 mt-1">⚠ Required to submit</p>}
              </div>
              <div>
                <Label>Additional Attachments <span className="text-xs text-muted-foreground">(Optional, max 5MB each)</span></Label>
                <Input type="file" accept="image/*,application/pdf" multiple onChange={handleExtraAttachments} data-testid="exp-extra-attachments" />
                {extraAttachments.length > 0 && (
                  <ul className="text-xs space-y-1 mt-1" data-testid="exp-attached-list">
                    {extraAttachments.map((a, i) => (
                      <li key={i} className="flex items-center justify-between bg-muted/30 p-1.5 rounded">
                        <span className="truncate">📎 {a.name}</span>
                        <button type="button" className="text-destructive ml-2" onClick={() => setExtraAttachments(extraAttachments.filter((_, idx) => idx !== i))}>×</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid="submit-exp-confirm">{submitting && <Loader2 size={14} className="animate-spin mr-1" />}Submit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">My Expenses</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Amount</TableHead><TableHead>Description</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {expenses.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No expenses yet</TableCell></TableRow>}
              {expenses.map(e => (
                <TableRow key={e.id} data-testid={`exp-row-${e.id}`}>
                  <TableCell>{new Date(e.created_at).toLocaleDateString()}</TableCell>
                  <TableCell><Badge variant="outline">{e.category}</Badge></TableCell>
                  <TableCell className="font-medium">{e.currency} {e.amount?.toLocaleString()}</TableCell>
                  <TableCell className="max-w-xs truncate">{e.description}</TableCell>
                  <TableCell><Badge variant={e.status === 'approved' ? 'default' : e.status === 'rejected' ? 'destructive' : 'secondary'}>{e.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyExpenses;
